import { Component, ReactNode, ReactElement, CSSProperties } from 'react';
import _ from 'lodash';
import "./AdvanceLayout.less";
import { getZDepthShadow } from '@Utils/Colors';
import { BoardTheming } from '@UI/Dashboard/BoardTheming';
import { IComponentSizeProp } from '@Primitive/Interfaces';
import { isDefined } from '@Utils/Micro';
import { Props } from '@Utils/ReactProps';
// ================================================================================================
export type RenderCB = (width: number, height: number) => ReactNode;
// ------------------------------------------------------------------------------------------------
export interface ToolbarSettings {
    /**
     * Left/Up Elements
     */
    LeftElements?: ReactNode[];

    /**
     * Center Elements
     */
    CenterElements?: ReactNode[];

    /**
     * Right/Down Elements
     */
    RightElements?: ReactNode[];

    /**
     * The size of the toolbar in pixels.
     * Default: 35
     */
    Size?: number;

    /**
     * Override css settings
     */
    style?: CSSProperties;
}
// ------------------------------------------------------------------------------------------------
interface ToolbarRenderFields {
    width: number;
    offsetLeft: number;
    leftDrawerIcon: ReactNode[];
    rightDrawerIcon: ReactNode[];
}
// ------------------------------------------------------------------------------------------------
export interface DrawerSettings {
    /**
     * Render the body of drawer
     */
    Render: RenderCB;

    /**
     * The size of the drawer in pixels
     */
    Size: number;

    /**
     * Modal drawer is drawn over the main body and is not effect the layout sizes.
     * Default: true
     */
    IsModal?: boolean;

    /**
     * The drawer is static and can not closed from automatic UI element.
     * The developer need to add other way to close it.
     * If this is false then the `AdvanceLayout` component is going to add prober icons for open/close drawer.
     * Default: false
     */
    IsStatic?: boolean;

    /**
     * Open the modal in hovering of the icon and keep it open until you hover out from drawer.
     * This is valid only when you are in modal mode.
     * Default: false
     */
    IsHoverable?: boolean;

    /**
     * Define a toolbar for the drawer.
     * In the case of dynamic drawer the component is going to add extra icons for open/close drawers.
     */
    Toolbar?: ToolbarSettings;

    /**
     * Set the initial state of the drawer. 
     * In the case of static this became the actual state.
     */
    InitialOpen?: boolean;

    /**
     * The drawer is not overlab toolbars.
     * Default: false
     */
    InsideBody?: boolean;

    /**
     * Fill the height/width of the space that can accure
     * Valid only for modal mode.
     * Default: true
     */
    FillSpace?: boolean;

    /**
     * Instead of have open/close icon use only one like a dropdown 
     * This is valid for left/right drawers in modal state with inside body.
     */
    UseDropdownIcon?: boolean;

    /**
     * Override css settings
     */
    style?: CSSProperties;
}
interface DrawerSettingsInternal extends Partial<DrawerSettings> {
    isOpen: boolean;
    marginTop: number;
    marginLeft: number;
    bottom?: number;
    height?: number;
    width?: number;
    rightIcon?: ReactNode[];
    leftIcon?: ReactNode[];
}
// ------------------------------------------------------------------------------------------------
export interface AdvanceLayoutProps extends Props<AdvanceLayout>, IComponentSizeProp {
    /**
     * Define the settings of the main toolbar.
     */
    MainToolbar?: ToolbarSettings;

    /**
     * Define the settings of the bottom toolbar
     * Default Size: 25
     */
    BottomToolbar?: ToolbarSettings;

    /** 
     * Render the main body area.
     */
    MainBody: RenderCB;

    /**
     * Left drawer (Optional)
     */
    LeftDrawer?: DrawerSettings;

    /**
     * Secondary left drawer (Optional)
     */
    SecondaryLeftDrawer?: DrawerSettings;

    /**
     * Right drawer (Optional)
     */
    RightDrawer?: DrawerSettings;

    /**
     * Top drawer (Optional)
     */
    TopDrawer?: DrawerSettings;

    /**
     * Top drawer (Optional)
     */
    BottomDrawer?: DrawerSettings;

    /**
     * Override css settings
     */
    style?: CSSProperties;
}
// ------------------------------------------------------------------------------------------------
export interface AdvanceLayoutState {
    LeftDrawerIsOpen: boolean;
    SecondaryLeftDrawerIsOpen: boolean;
    RightDrawerIsOpen: boolean;
    TopDrawerIsOpen: boolean;
    BottomDrawerIsOpen: boolean;
}
// ================================================================================================
export class AdvanceLayout extends Component<AdvanceLayoutProps, AdvanceLayoutState>{
    // --------------------------------------------------------------------------------------------
    constructor(props: AdvanceLayoutProps) {
        super(props);

        this.state = {
            // Drawer states
            SecondaryLeftDrawerIsOpen: isDefined(this.props.SecondaryLeftDrawer) ? this.props.SecondaryLeftDrawer.InitialOpen : true,
            LeftDrawerIsOpen: isDefined(this.props.LeftDrawer) ? this.props.LeftDrawer.InitialOpen : true,
            RightDrawerIsOpen: isDefined(this.props.RightDrawer) ? this.props.RightDrawer.InitialOpen : true,
            TopDrawerIsOpen: isDefined(this.props.TopDrawer) ? this.props.TopDrawer.InitialOpen : true,
            BottomDrawerIsOpen: isDefined(this.props.BottomDrawer) ? this.props.BottomDrawer.InitialOpen : true,
        }
    }
    // --------------------------------------------------------------------------------------------
    toggleLeftDrawer = () => this.setState((s: AdvanceLayoutState) => { s.LeftDrawerIsOpen = !s.LeftDrawerIsOpen; return s; });
    toggleSecondaryLeftDrawer = () => this.setState((s: AdvanceLayoutState) => { s.SecondaryLeftDrawerIsOpen = !s.SecondaryLeftDrawerIsOpen; return s; });
    toggleRightDrawer = () => this.setState((s: AdvanceLayoutState) => { s.RightDrawerIsOpen = !s.RightDrawerIsOpen; return s; });
    toggleTopDrawer = () => this.setState((s: AdvanceLayoutState) => { s.TopDrawerIsOpen = !s.TopDrawerIsOpen; return s; });
    toggleBottomDrawer = () => this.setState((s: AdvanceLayoutState) => { s.BottomDrawerIsOpen = !s.BottomDrawerIsOpen; return s; });
    // --------------------------------------------------------------------------------------------
    render() {
        let mainToolbar: ToolbarRenderFields = {
            width: this.props.width,
            offsetLeft: 0,
            leftDrawerIcon: null,
            rightDrawerIcon: null
        };
        let bottomToolbar: ToolbarRenderFields = {
            width: this.props.width,
            offsetLeft: 0,
            leftDrawerIcon: null,
            rightDrawerIcon: null
        };
        let mainBody = {
            height: this.props.height,
            width: this.props.width,
            offsetLeft: 0,
            offsetTop: 0
        };

        // Update sizes based on user maintoolbar
        let mainToolbarSettings: ToolbarSettings = _.defaults({},
            this.props.MainToolbar,
            {
                CenterElements: [],
                LeftElements: [],
                RightElements: [],
                Size: 35,
                style: {}
            });
        mainBody.height -= mainToolbarSettings.Size;

        let bottomToolbarSettings: ToolbarSettings = _.defaults({},
            this.props.BottomToolbar,
            {
                CenterElements: [],
                LeftElements: [],
                RightElements: [],
                Size: 25,
                style: {}
            });

        // Handle bottom drawer
        let bottomDrawerSettings: DrawerSettingsInternal = { isOpen: false, marginTop: 0, marginLeft: 0, width: this.props.width, bottom: 0 };
        if (isDefined(this.props.BottomDrawer)) {
            bottomDrawerSettings = _.defaultsDeep(bottomDrawerSettings,
                this.props.BottomDrawer,
                {
                    IsModal: true,
                    IsStatic: false,
                    InsideBody: false,
                    IsHoverable: false,
                    FillSpace: true,
                    Toolbar: {
                        CenterElements: [],
                        LeftElements: [],
                        RightElements: [],
                        Size: 35,
                        style: {}
                    }
                } as Partial<DrawerSettings>)

            this.drawerTopDownCalculations(false, bottomDrawerSettings, bottomToolbarSettings, mainBody, bottomToolbar);
        }

        // allocate space for bottom toolbar
        let bottomToolbarIsActive = bottomToolbarSettings.CenterElements.length > 0 ||
            bottomToolbarSettings.LeftElements.length > 0 ||
            bottomToolbarSettings.RightElements.length > 0 ||
            bottomToolbar.leftDrawerIcon != null ||
            bottomToolbar.rightDrawerIcon != null;
        if (bottomToolbarIsActive)
            mainBody.height -= bottomToolbarSettings.Size;

        // In non-modal case move the drawer over the toolbar if is exist
        if (bottomToolbarIsActive && !bottomDrawerSettings.IsModal) {
            bottomDrawerSettings.bottom = bottomToolbarSettings.Size;
        }

        // Handle left drawer
        let leftDrawerSettings: DrawerSettingsInternal = this.getLeftRightDrawer(true, false, this.props.LeftDrawer, mainToolbarSettings, mainBody, mainToolbar);

        // Handle secondary left drawer
        let secondaryLeftDrawerSettings: DrawerSettingsInternal = this.getLeftRightDrawer(false, true, this.props.SecondaryLeftDrawer, mainToolbarSettings, mainBody, mainToolbar);

        // Handle left & secondary drawers
        this.drawersLeftCalculations(leftDrawerSettings, secondaryLeftDrawerSettings);

        // Handle right drawer
        let rightDrawerSettings: DrawerSettingsInternal = this.getLeftRightDrawer(false, false, this.props.RightDrawer, mainToolbarSettings, mainBody, mainToolbar);

        return <div
            className="advance-layout"
            style={{
                ...this.props.style,
                width: this.props.width,
                height: this.props.height
            }}>
            {(!leftDrawerSettings.IsHoverable) ? this.drawerRender("left", leftDrawerSettings) : null}
            {this.drawerRender("left-secondary", secondaryLeftDrawerSettings)}
            {this.drawerRender("right", rightDrawerSettings)}
            <div
                key="top-toolbar"
                id="toolbar"
                className="advance-toolbar"
                style={{
                    ...mainToolbarSettings.style,
                    height: mainToolbarSettings.Size,
                    width: mainToolbar.width,
                    maxWidth: mainToolbar.width,
                    left: mainToolbar.offsetLeft,
                    lineHeight: mainToolbarSettings.Size + "px"
                }}>
                <div key='top-left-elements'>
                    {
                        (leftDrawerSettings.IsHoverable) ?
                            <div key='hover-menu' className="hover-menu" >
                                {mainToolbar.leftDrawerIcon}
                                {this.drawerRender("left", leftDrawerSettings)}
                            </div>
                            :
                            mainToolbar.leftDrawerIcon
                    }
                    {mainToolbarSettings.LeftElements}
                </div>
                <div key='center-left-elements'>{mainToolbarSettings.CenterElements}</div>
                <div key='right-left-elements'>{mainToolbarSettings.RightElements}{mainToolbar.rightDrawerIcon}</div>
            </div>
            <div key='main-body'
                id="main-body"
                style={{
                    width: mainBody.width,
                    left: mainBody.offsetLeft,
                    overflow: "auto"
                }}>
                {this.props.MainBody(mainBody.width, mainBody.height)}
            </div>
            {this.drawerRender("bottom", bottomDrawerSettings)}
            {
                (bottomToolbarIsActive) ?
                    <div
                        key='bottom-toolbar'
                        id="toolbar"
                        className="advance-toolbar"
                        style={{
                            ...bottomToolbarSettings.style,
                            height: bottomToolbarSettings.Size,
                            width: bottomToolbar.width,
                            left: bottomToolbar.offsetLeft,
                            lineHeight: bottomToolbarSettings.Size + "px",
                            bottom: 0,
                            position: "absolute"
                        }}>
                        <div key='bottom-left-elements'>{bottomToolbar.leftDrawerIcon} {bottomToolbarSettings.LeftElements}</div>
                        <div key='bottom-center-elements'>{bottomToolbarSettings.CenterElements}</div>
                        <div key='bottom-right-elements'>{bottomToolbarSettings.RightElements}{bottomToolbar.rightDrawerIcon}</div>
                    </div>
                    : null
            }
        </div >
    }
    // --------------------------------------------------------------------------------------------
    private drawerRender(type: "left" | "left-secondary" | "right" | "top" | "bottom", drawerSettings: DrawerSettingsInternal): ReactNode {
        if (!drawerSettings.isOpen)
            return null;
        const theme = BoardTheming.GetActiveTheme();


        if (type == "bottom" || type == "top") {
            return <div
                key={type + ((drawerSettings.IsModal) ? "-drawer-modal" : "-drawer")}
                id={type + ((drawerSettings.IsModal) ? "-drawer-modal" : "-drawer")}
                style={{
                    boxShadow: getZDepthShadow(theme.drawer.shadowLevel),
                    ...drawerSettings.style,
                    height: drawerSettings.Size,
                    width: drawerSettings.width,
                    marginLeft: drawerSettings.marginLeft,
                    bottom: drawerSettings.bottom
                }}>
                <div key='toolbar' id="toolbar" className="advance-toolbar"
                    style={{
                        backgroundColor: theme.drawer.toolbarBackgroundColor,
                        color: theme.drawer.toolbarFontColor,
                        boxShadow: getZDepthShadow(theme.drawer.toolbarShadowLevel),
                        ...drawerSettings.Toolbar.style,
                        height: drawerSettings.Toolbar.Size,
                        lineHeight: drawerSettings.Toolbar.Size + "px"
                    }}>
                    <div key='leftIcon'>{drawerSettings.leftIcon}{drawerSettings.Toolbar.LeftElements}</div>
                    <div key='CenterElements'>{drawerSettings.Toolbar.CenterElements}</div>
                    <div key='RightElements'>{drawerSettings.Toolbar.RightElements}{drawerSettings.rightIcon}</div>
                </div>
                {drawerSettings.Render(drawerSettings.width, drawerSettings.Size - drawerSettings.Toolbar.Size)}
            </div>
        }

        return <div
            key={type + ((drawerSettings.IsModal) ? "-drawer-modal" : "-drawer")}
            id={type + ((drawerSettings.IsModal) ? "-drawer-modal" : "-drawer")}
            style={{
                boxShadow: getZDepthShadow(theme.drawer.shadowLevel),
                ...drawerSettings.style,
                width: drawerSettings.Size,
                maxHeight: drawerSettings.height,
                height: (drawerSettings.FillSpace) ? drawerSettings.height : undefined,
                marginTop: (drawerSettings.IsHoverable) ? undefined : drawerSettings.marginTop,
                marginLeft: drawerSettings.marginLeft
            }}>
            <div key='toolbar' id="toolbar" className="advance-toolbar"
                style={{
                    backgroundColor: theme.drawer.toolbarBackgroundColor,
                    color: theme.drawer.toolbarFontColor,
                    boxShadow: getZDepthShadow(theme.drawer.toolbarShadowLevel),
                    ...drawerSettings.Toolbar.style,
                    height: drawerSettings.Toolbar.Size,
                    lineHeight: drawerSettings.Toolbar.Size + "px"
                }}>
                <div key='leftIcon'>{drawerSettings.leftIcon}{drawerSettings.Toolbar.LeftElements}</div>
                <div key='CenterElements'>{drawerSettings.Toolbar.CenterElements}</div>
                <div key='RightElements'>{drawerSettings.Toolbar.RightElements}{drawerSettings.rightIcon}</div>
            </div>
            <div
                key='body'
                style={{
                    overflow: "auto",
                    maxHeight: drawerSettings.height - drawerSettings.Toolbar.Size,
                    height: (drawerSettings.FillSpace) ? (drawerSettings.height - drawerSettings.Toolbar.Size) : undefined,
                    width: drawerSettings.Size,
                }}>
                {drawerSettings.Render(drawerSettings.Size, drawerSettings.height - drawerSettings.Toolbar.Size)}
            </div>
        </div>
    }
    // --------------------------------------------------------------------------------------------
    private getLeftRightDrawer(
        isLeft: boolean,
        isSecondaryLeft: boolean,
        drawerSettings: DrawerSettings,
        mainToolbarSettings: ToolbarSettings,
        mainBody: { height: number; width: number; offsetLeft: number; offsetTop: number; },
        mainToolbar: ToolbarRenderFields
    ): DrawerSettingsInternal {

        let internalSettings: DrawerSettingsInternal = { isOpen: false, marginTop: 0, marginLeft: 0, height: (mainBody.height + mainToolbarSettings.Size) };

        if (isDefined(drawerSettings)) {
            internalSettings = _.defaultsDeep(internalSettings,
                drawerSettings,
                {
                    IsModal: true,
                    IsStatic: false,
                    InsideBody: false,
                    IsHoverable: false,
                    FillSpace: true,
                    UseDropdownIcon: false,
                    Toolbar: {
                        CenterElements: [],
                        LeftElements: [],
                        RightElements: [],
                        Size: 35,
                        style: {}
                    }
                } as Partial<DrawerSettings>);

            // we can't have no modal and be hovable
            if (!internalSettings.IsModal) {
                internalSettings.IsHoverable = false;
                internalSettings.FillSpace = true;
                internalSettings.UseDropdownIcon = false;
            }

            if (internalSettings.IsHoverable && !drawerSettings.FillSpace)
                internalSettings.FillSpace = false;

            this.drawerLeftRightCalculations(isLeft, isSecondaryLeft, internalSettings, mainToolbarSettings, mainBody, mainToolbar);
        }

        return internalSettings;
    }
    // --------------------------------------------------------------------------------------------
    private drawerLeftRightCalculations(
        isLeft: boolean,
        isSecondaryLeft: boolean,
        drawerSettings: DrawerSettingsInternal,
        mainToolbarSettings: ToolbarSettings,
        mainBody: { height: number; width: number; offsetLeft: number; offsetTop: number; },
        mainToolbar: ToolbarRenderFields
    ): void {
        // If is not static add icon in toolbars
        if (!drawerSettings.IsStatic) {

            if (isLeft)
                drawerSettings.isOpen = this.state.LeftDrawerIsOpen;
            else if (isSecondaryLeft)
                drawerSettings.isOpen = this.state.SecondaryLeftDrawerIsOpen;
            else
                drawerSettings.isOpen = this.state.RightDrawerIsOpen;

            // Add icons for the open/close
            if (isLeft || isSecondaryLeft) {
                const key = isLeft ? "icon-left" : "icon-left-secondary";
                let leftDrawerIcon: ReactElement<any> = null;
                let rightIcon: ReactElement<any> = null;
                if (drawerSettings.IsHoverable) {
                    leftDrawerIcon = <i key={key} id="icons" className={"fa fa-chevron-circle-down"} />;
                } else if (drawerSettings.UseDropdownIcon) {
                    const onClick = isLeft ? this.toggleLeftDrawer : this.toggleSecondaryLeftDrawer;
                    if (drawerSettings.isOpen)
                        leftDrawerIcon = <i key={key} id="icons" className={"fa fa-chevron-circle-up"} onClick={onClick} />;
                    else
                        leftDrawerIcon = <i key={key} id="icons" className={"fa fa-chevron-circle-down"} onClick={onClick} />;
                } else {
                    const onClick = isLeft ? this.toggleLeftDrawer : this.toggleSecondaryLeftDrawer;
                    if (drawerSettings.isOpen)
                        rightIcon = <i key={key} id="icons" className={"fa fa-chevron-left"} onClick={onClick} />;
                    else
                        leftDrawerIcon = <i key={key} id="icons" className={"fa fa-chevron-right"} onClick={onClick} />;
                }

                if (isDefined(leftDrawerIcon)) {
                    if (!isDefined(mainToolbar.leftDrawerIcon))
                        mainToolbar.leftDrawerIcon = [leftDrawerIcon];
                    else
                        mainToolbar.leftDrawerIcon.push(leftDrawerIcon);
                }
                else if (isDefined(rightIcon)) {
                    if (!isDefined(drawerSettings.rightIcon))
                        drawerSettings.rightIcon = [rightIcon];
                    else
                        drawerSettings.rightIcon.push(rightIcon);
                }
            }
            else {
                if (drawerSettings.isOpen)
                    drawerSettings.leftIcon = [<i id="icons" className={"fa fa-chevron-right"} onClick={this.toggleRightDrawer} />];
                else
                    mainToolbar.rightDrawerIcon = [<i id="icons" className={"fa fa-chevron-left"} onClick={this.toggleRightDrawer} />];
            }
        } else {
            drawerSettings.isOpen = isDefined(drawerSettings.InitialOpen) ? drawerSettings.InitialOpen : true;
        }

        // shift drawer to be inside of main body
        if (drawerSettings.InsideBody) {
            if (!drawerSettings.IsHoverable) // the drawer is part of the toolbar then
                drawerSettings.marginTop = mainToolbarSettings.Size;
            drawerSettings.height = mainBody.height;
        } else {
            drawerSettings.marginTop = 0;
            drawerSettings.height = mainBody.height + mainToolbarSettings.Size;
        }

        // in modal case have a space in top/bottom
        if (drawerSettings.IsModal) {
            drawerSettings.marginTop += 5;
            drawerSettings.height -= 10;
        }

        // Resize the main body if is not modal and is open
        if (!drawerSettings.IsModal && drawerSettings.isOpen) {
            mainBody.width -= drawerSettings.Size;
            if (isLeft || isSecondaryLeft)
                mainBody.offsetLeft += drawerSettings.Size;
            // When is in inside don't resize the toolbar
            if (!drawerSettings.InsideBody) {
                mainToolbar.width -= drawerSettings.Size;
                if (isLeft || isSecondaryLeft)
                    mainToolbar.offsetLeft += drawerSettings.Size;
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Fix left margin at left drawers.
     * 
     * if the drawer A occupies space and the drawer B is open
     * add left margin to drawer B
     */
    private drawersLeftCalculations(
        leftDrawer: DrawerSettingsInternal,
        secondaryDrawer: DrawerSettingsInternal
    ): void {

        // try fix secondary drawer first
        if (leftDrawer.isOpen) {
            // if left & secondary drawers occupy space
            if (secondaryDrawer.isOpen &&
                !leftDrawer.IsModal &&
                !leftDrawer.InsideBody &&
                !secondaryDrawer.IsModal) {
                // add left margin to secondary drawer
                secondaryDrawer.marginLeft = leftDrawer.Size;
                return;
            }
        }

        // try fix left drawer
        if (secondaryDrawer.isOpen) {

            // if secondary drawers occupies space, but left drawer is modal 
            if (!secondaryDrawer.IsModal &&
                !secondaryDrawer.InsideBody &&
                leftDrawer.isOpen &&
                leftDrawer.IsModal) {
                // add left margin to secondary drawer
                leftDrawer.marginLeft = secondaryDrawer.Size;
                return;
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    private drawerTopDownCalculations(
        isTop: boolean,
        drawerSettings: DrawerSettingsInternal,
        mainToolbarSettings: ToolbarSettings,
        mainBody: { height: number; width: number; offsetLeft: number; offsetTop: number; },
        toolbar: ToolbarRenderFields) {
        // If is not static add icon in toolbars
        if (!drawerSettings.IsStatic) {
            drawerSettings.isOpen = (isTop) ? this.state.TopDrawerIsOpen : this.state.BottomDrawerIsOpen;
            // Add icons for the open/close
            if (isTop) {
                if (drawerSettings.isOpen)
                    drawerSettings.rightIcon = [<i key='toggle-icon' id="icons" className={"fa fa-chevron-up"} onClick={this.toggleTopDrawer} />];
                else
                    toolbar.leftDrawerIcon = [<i key='toggle-icon' id="icons" className={"fa fa-chevron-down"} onClick={this.toggleTopDrawer} />];
            } else {
                if (drawerSettings.isOpen)
                    drawerSettings.rightIcon = [<i key='toggle-icon' id="icons" className={"fa fa-chevron-down"} onClick={this.toggleBottomDrawer} />];
                else
                    toolbar.rightDrawerIcon = [<i key='toggle-icon' id="icons" className={"fa fa-chevron-up"} onClick={this.toggleBottomDrawer} />];
            }
        } else {
            drawerSettings.isOpen = isDefined(drawerSettings.InitialOpen) ? drawerSettings.InitialOpen : true;
        }

        // shift drawer to be inside of main body
        if (drawerSettings.InsideBody) {
            drawerSettings.marginTop = mainToolbarSettings.Size;
            //   drawerSettings.width = this.props.width - mainToolbarSettings.Size;
        }

        // in modal case have a space in top/bottom
        if (drawerSettings.IsModal) {
            drawerSettings.marginLeft += 5;
            drawerSettings.width -= 10;
        }

        // Resize the main body if is not modal and is open
        if (!drawerSettings.IsModal && drawerSettings.isOpen) {
            mainBody.height -= drawerSettings.Size;
            if (isTop)
                mainBody.offsetTop += drawerSettings.Size;

            // TODO: support inside/ontop of toolbar
        }
    }
    // --------------------------------------------------------------------------------------------

}
// ================================================================================================
export interface ToolbarProps {
    /**
     * Override toolbar style
     */
    style?: CSSProperties,

    /**
     * Define the height of the toolbar
     */
    height: number;

    /**
     * Left/Up Elements
     */
    LeftElements?: ReactNode[];

    /**
     * Center Elements
     */
    CenterElements?: ReactNode[];

    /**
     * Right/Down Elements
     */
    RightElements?: ReactNode[];
}
export var RenderToolbar = (props: ToolbarProps) => {
    return <div id="toolbar" className="advance-toolbar" style={{
        ...props.style,
        height: props.height,
        lineHeight: props.height + "px"
    }}>
        <div key="left">{props.LeftElements}</div>
        <div key="center">{props.CenterElements}</div>
        <div key="right">{props.RightElements}</div>
    </div>
};
// ================================================================================================